/**
 * WebSocket hook for real-time player state updates
 */

import { useEffect, useRef, useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { playerKeys } from './usePlayer';
import type { PlaybackStateResponse } from '../types';
import { getWsUrl } from '../client';
import { createLogger } from '@/lib/logger';

const log = createLogger({ namespace: 'WebSocket' });

export interface PlayerWSMessage {
  type: 'connected' | 'state' | 'no_device' | 'error' | 'ping' | 'pong' | 'closing';
  timestamp: number;
  data?: PlaybackStateResponse;
  message?: string;
  code?: string;
  details?: Record<string, unknown>;
}

export interface UsePlayerWebSocketOptions {
  enabled?: boolean;
  baseUrl?: string;
  getAccessToken: () => string | null;
  onError?: (error: Error) => void;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
}

export function usePlayerWebSocket(options: UsePlayerWebSocketOptions) {
  const {
    enabled = true,
    baseUrl,
    getAccessToken,
    onError,
    reconnectInterval = 5000,
    maxReconnectAttempts = 5,
  } = options;

  // Use dynamic WebSocket URL if not provided
  const wsBaseUrl = baseUrl || getWsUrl();

  const wsRef = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const reconnectAttemptsRef = useRef(0);
  const enabledRef = useRef(enabled); // Track enabled state for closure
  const queryClient = useQueryClient();

  // Keep ref in sync with prop
  useEffect(() => {
    enabledRef.current = enabled;
  }, [enabled]);

  const [connectionState, setConnectionState] = useState<
    'disconnected' | 'connecting' | 'connected' | 'error'
  >('disconnected');

  const connect = useCallback(() => {
    if (!enabled) return;

    const token = getAccessToken();
    if (!token) {
      log.warn('No access token available for connection');
      return;
    }

    // Clean up existing connection
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }

    setConnectionState('connecting');

    // Create WebSocket connection with JWT token
    const wsUrl = `${wsBaseUrl.replace(/^http/, 'ws')}/v1/player/ws?token=${token}`;
    const ws = new WebSocket(wsUrl);

    ws.onopen = () => {
      log.info('Connected');
      setConnectionState('connected');
      reconnectAttemptsRef.current = 0;
    };

    ws.onmessage = (event) => {
      try {
        const message: PlayerWSMessage = JSON.parse(event.data);

        switch (message.type) {
          case 'connected':
            // Connection confirmation received
            break;

          case 'state':
            // Update React Query cache with new state
            if (message.data) {
              queryClient.setQueryData(playerKeys.state(), message.data);
            }
            break;

          case 'no_device':
            // Update cache to indicate no active device
            queryClient.setQueryData(playerKeys.state(), null);
            break;

          case 'error':
            log.error('Error message:', message);
            if (onError) {
              onError(new Error(message.message || 'Unknown error'));
            }
            break;

          case 'ping':
            // Respond to ping with pong
            ws.send(JSON.stringify({ type: 'pong', timestamp: Date.now() }));
            break;

          case 'closing':
            log.info('Server closing connection:', message.message);
            break;
        }
      } catch (error) {
        log.error('Error parsing WebSocket message:', error);
      }
    };

    ws.onerror = (error) => {
      log.error('Connection error:', error);
      setConnectionState('error');
      if (onError) {
        onError(new Error('WebSocket connection error'));
      }
    };

    ws.onclose = () => {
      setConnectionState('disconnected');
      wsRef.current = null;

      // Attempt reconnection if not manually closed and still enabled
      // Use ref to get current enabled value, not stale closure value
      if (enabledRef.current && reconnectAttemptsRef.current < maxReconnectAttempts) {
        reconnectAttemptsRef.current++;
        log.info(
          `Reconnecting (attempt ${reconnectAttemptsRef.current}/${maxReconnectAttempts})`
        );
        reconnectTimeoutRef.current = setTimeout(connect, reconnectInterval);
      } else if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
        log.error('Max reconnect attempts reached');
      }
    };

    wsRef.current = ws;
  }, [
    enabled,
    wsBaseUrl,
    getAccessToken,
    onError,
    reconnectInterval,
    maxReconnectAttempts,
    queryClient,
  ]);

  // Connect on mount and when enabled changes
  useEffect(() => {
    log.debug('useEffect: enabled changed to', enabled);
    if (enabled) {
      connect();
    } else {
      // Explicitly disconnect when disabled
      log.debug('Disconnecting due to enabled=false');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = undefined;
      }
      if (wsRef.current) {
        log.debug('Closing WebSocket connection');
        wsRef.current.close();
        wsRef.current = null;
      }
    }

    return () => {
      // Cleanup on unmount
      log.debug('useEffect cleanup');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]); // Only re-run when enabled changes, not when connect changes

  // Manual reconnect function
  const reconnect = useCallback(() => {
    reconnectAttemptsRef.current = 0;
    connect();
  }, [connect]);

  // Disconnect function
  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
  }, []);

  return {
    connectionState,
    reconnect,
    disconnect,
  };
}
